import { useState, useEffect } from "react";
import { calcSimilarity } from "../utils/similarity";
import useAuthorBooks from "../hooks/useAuthorBooks";
import BookHero from "./BookHero";
import BookReaders from "./BookReaders";
import BookQuotes from "./BookQuotes";
import BookMemos from "./BookMemos";
import BookDiscussion from "./BookDiscussion";
import EmotionCoordinate from "./EmotionCoordinate";
import BookCoLoan from "./BookCoLoan";
import { isbn13 } from "../api/books";
import "./BookScreen.css";
import { readBooks } from "../utils/bookState";

const BookScreen = ({ bookId, books, allBooks = [], externalBook, users, currentUser, viewAsUserId, onBack, onUserClick, onBookUpdated, onBookCompleted, onBookRemoved, onMemoAdded, onBookClick, onAddToMine, onGoHome, openTab, clearOpenTab, focusId, clearFocus, onGoMine }) => {
  /* 탭을 지정해 들어오면 그 탭으로 연다. 초기값에 바로 넣어야 '독자' 탭이
     한 번 번쩍였다가 바뀌지 않는다. */
  const [activeTab, setActiveTab] = useState(openTab || "readers");
  useEffect(() => {
    if (!openTab) return;
    setActiveTab(openTab);
    clearOpenTab?.();
  }, [openTab, clearOpenTab]);

  const book = books.find(b => b.id === bookId) || allBooks.find(b => b.id === bookId) || (externalBook && externalBook.id === bookId ? externalBook : null);
  const { authorBooks, authorBooksLoading } = useAuthorBooks(activeTab, book);

  if (!book) return null;
  const isMyBook = books.some(b => b.id === bookId);
  const isOwned = !currentUser?.isAnonymous && isMyBook;

  // 담아둔 책은 유사도 계산에서 뺀다.
  const myBookIds = readBooks(books, currentUser?.uid).map(b => b.id);
  const readers = (users || [])
    .filter(u => (u.bookIds || []).includes(bookId))
    .map(u => ({ ...u, similarity: calcSimilarity(myBookIds, u.bookIds || []) }));

  return (
    <div className="bp-layer bp-layer-full">
      {/* 책 화면에서 나가는 곳은 내 행성이다. '돌아가기' 는 등록 창이나 그 전
          화면으로 가서 어디로 가는지 모호했다.
          다른 사람 행성에서 이 책을 열었을 때만 '돌아가기' 로 둔다 — 거기서
          내 행성으로 보내면 보던 행성으로 돌아갈 길을 잃는다.
          게스트는 내 행성이 없어 그대로 '돌아가기'. */}
      {(() => {
        const uid = currentUser?.uid;
        const fromOtherPlanet = !!viewAsUserId && viewAsUserId !== "me" && viewAsUserId !== uid;
        const toMine = onGoMine && !fromOtherPlanet && !currentUser?.isAnonymous;
        return toMine ? (
          <div className="back-bar" onClick={onGoMine}>← <span>내 행성으로 돌아가기</span></div>
        ) : (
          <div className="back-bar" onClick={onBack}>← <span>돌아가기</span></div>
        );
      })()}
      <div className="book-hero">
        <BookHero
          book={book}
          bookId={bookId}
          isOwned={isOwned}
          isMyBook={isMyBook}
          currentUser={currentUser}
          viewAsUserId={viewAsUserId}
          users={users}
          onBookUpdated={onBookUpdated}
          onBookCompleted={onBookCompleted}
          onBookRemoved={onBookRemoved}
          onMemoAdded={onMemoAdded}
          onAddToMine={onAddToMine}
          onGoHome={onGoHome}
          onBookClick={onBookClick}
          onBack={onBack}
        />
      </div>

      <EmotionCoordinate
        book={book}
        currentUser={currentUser}
        viewAsUserId={viewAsUserId}
        isOwned={isOwned}
        users={users}
        onMemoAdded={onMemoAdded}
      />

      <div className="book-tabs">
        <button className={`book-tab${activeTab==="readers"?" active":""}`} onClick={()=>setActiveTab("readers")}>
          👥 독자 {readers.length}
        </button>
        {isOwned && !viewAsUserId && (
          <button className={`book-tab${activeTab==="memos"?" active":""}`} onClick={()=>setActiveTab("memos")}>
            📝 문장과 기록
          </button>
        )}
        <button className={`book-tab${activeTab==="public"?" active":""}`} onClick={()=>setActiveTab("public")}>
          💬 이야기 나누기
        </button>
        <button className={`book-tab${activeTab==="author"?" active":""}`} onClick={()=>setActiveTab("author")}>
          📚 작가의 다른 책
        </button>
        {/* ISBN 이 있어야 도서관 기록을 찾을 수 있다. 없으면 탭을 내지 않는다. */}
        {isbn13(book.isbn) && (
          <button className={`book-tab${activeTab==="coloan"?" active":""}`} onClick={()=>setActiveTab("coloan")}>
            🔁 함께 빌린 책
          </button>
        )}
      </div>

      {activeTab === "readers" && (
        <BookReaders
          book={book}
          readers={readers}
          currentUser={currentUser}
          onUserClick={onUserClick}
        />
      )}

      {/* 문장이 기록보다 위에 온다. 문장은 책의 말이고 기록은 내 말이다. */}
      {activeTab === "memos" && (
        <>
          <BookQuotes book={book} currentUser={currentUser} users={users} />
          <BookMemos book={book} currentUser={currentUser} onMemoAdded={onMemoAdded} />
        </>
      )}

      {activeTab === "public" && (
        <>
          {/* 공개한 문장은 남의 행성에서 그 책을 열어도 보여야 한다.
              '문장과 기록' 은 내 책일 때만 나오는 탭이라 거기만 두면
              공개해도 아무도 못 본다. */}
          <BookQuotes book={book} currentUser={currentUser} users={users} mode="public"
            focusId={focusId} clearFocus={clearFocus} />
          <BookDiscussion book={book} currentUser={currentUser} users={users} onMemoAdded={onMemoAdded}
            focusId={focusId} clearFocus={clearFocus} />
        </>
      )}

      {activeTab === "coloan" && (
        <BookCoLoan book={book} allBooks={allBooks} onBookClick={onBookClick} />
      )}

      {activeTab === "author" && (
        <div className="author-books-wrap">
          <div className="section-label" style={{marginTop:"1rem"}}>{book.author}의 다른 책</div>
          {authorBooksLoading ? (
            <div className="memo-empty">검색 중...</div>
          ) : authorBooks.length === 0 ? (
            <div className="memo-empty">다른 책을 찾지 못했어요</div>
          ) : (
            <div className="author-books-grid">
              {authorBooks.map((ab, i) => {
                const inApp = !ab.isExternal ? ab : allBooks.find(b => b.title === ab.title);
                return (
                  <div
                    key={ab.id || i}
                    className="author-book-card"
                    style={{ animationDelay: `${i * 0.06}s` }}
                    onClick={() => {
                      if (inApp && !inApp.isExternal && onBookClick) onBookClick(inApp.id);
                    }}
                  >
                    <div className="author-book-cover">
                      {ab.cover
                        ? <img src={ab.cover} alt={ab.title} />
                        : <span>📖</span>
                      }
                    </div>
                    <div className="author-book-info">
                      <div className="author-book-title">{ab.title}</div>
                      {ab.publishedDate && <div className="author-book-year">{ab.publishedDate}</div>}
                      {inApp && !inApp.isExternal
                        ? <div className="author-book-badge in-app">앱에 등록됨</div>
                        : <div className="author-book-badge external">외부 검색</div>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookScreen;
